import Button, { type ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import DigibookIcon from '@pelckmans/elementis/icons/digibook';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AccountContext from '../../../contexts/AccountContext';
import { type App } from '../api/appsApi';
import { buildCodexUrl, getDisabledTooltip, isOldDigibookApp } from '../utils/digibookAppUtils';
import useAnalytics from '../../../hooks/useAnalytics';
import { ANALYTICS_EVENT_TYPES } from '../../../constants/analyticsEvents';

const getHref = (digibookApp: App, accountProvider?: string) => {
  if (digibookApp.platformUrl) return digibookApp.platformUrl;
  if (digibookApp.linkedApp)
    return buildCodexUrl({
      digibookId: digibookApp.linkedApp.id,
      moduleId: digibookApp.moduleId,
      accountProvider,
    });
};

export default function DigibookButton({ digibookApps, size }: { digibookApps: App[]; size: ButtonProps['size'] }) {
  const { t } = useTranslation();
  const { account } = useContext(AccountContext);
  const analytics = useAnalytics();

  const captureEvent = (digibook: App) => {
    analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.DIGIBOOK_ACCESSED,
      objectId: isOldDigibookApp(digibook) ? digibook.platformUrl : digibook.linkedApp!.id,
    });

    analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.APP_ACCESSED,
      objectId: digibook.id,
    });
  };

  if (digibookApps.length === 0) return null;

  if (digibookApps.length === 1) {
    const digibookApp = digibookApps[0];
    const disabledTooltip = getDisabledTooltip(digibookApp);
    const href = getHref(digibookApp, account?.provider) || '';

    return (
      <Button
        key={digibookApp.id}
        as="link"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        prominence="secondary"
        size={size}
        startIcon={<DigibookIcon data-testid="digibook-icon" />}
        disabled={!!disabledTooltip}
        tooltip={disabledTooltip ? t(disabledTooltip) : undefined}
        onClick={() => captureEvent(digibookApp)}
      >
        {digibookApp.linkedApp?.manualIncluded
          ? t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK_WITH_MANUAL')
          : t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK')}
      </Button>
    );
  }

  return (
    <ButtonDropdown
      title={t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK')}
      size={size}
      key={digibookApps[0].id}
      buttonProps={{
        prominence: 'secondary',
        startIcon: <DigibookIcon data-testid="digibook-icon" />,
      }}
      disableBackgroundScroll
    >
      {digibookApps.map(digibookApp => {
        const disabledTooltip = getDisabledTooltip(digibookApp);
        const disabledProps = disabledTooltip ? ({ disabled: true, tooltip: t(disabledTooltip) } as const) : {};
        const subText = digibookApp.linkedApp?.manualIncluded
          ? t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.MANUAL_INCLUDED')
          : undefined;

        return (
          <ButtonDropdown.Item
            as="link"
            href={getHref(digibookApp, account?.provider)}
            target="_blank"
            rel="noopener noreferrer"
            key={digibookApp.id}
            value={digibookApp.id}
            text={`${digibookApp.module.catalogName}: ${digibookApp.name}`}
            subText={subText}
            onClick={() => captureEvent(digibookApp)}
            {...disabledProps}
          />
        );
      })}
    </ButtonDropdown>
  );
}
